/**
 * Render-transport: SignalR-hub (ws.cloudrenderer.eu).
 *
 * De hub duwt het resultaat terug via 'ReceiveMessage'. Er wordt dus niet
 * gepolld; we schrijven ons in op een requestString en wachten op de push.
 *
 * De hub accepteert elke tenantId (IdentifyClientForTenant / MakeRenderForTenant),
 * dus een nieuwe klant heeft hier geen code nodig, alleen een tenant.config.js.
 */

export function createSignalRTransport(cfg) {
    const HUB_URL = cfg.hub.url;
    const FILE_BASE = cfg.hub.fileBase;
    // Onder welke naam deze frontend zich bij de hub meldt. Meestal gelijk aan
    // cfg.id, maar niet altijd: delen twee merken een worker, dan delen ze ook
    // de tenant en staat die naam apart in hub.tenant.
    const TENANT = cfg.hub.tenant || cfg.id;
    const TIMEOUT_MS = cfg.hub.timeoutMs || 120000;

    let connection = null;
    let ready = null; // Promise, resolve zodra start() klaar is

    // requestString -> { onReady, onTimeout, timeoutHandle }
    const subscribers = new Map();

    function init() {
        if (ready) return ready;
        if (typeof signalR === 'undefined') {
            console.error('[SignalR] client niet geladen — verwacht signalr.min.js vóór configurator.js');
            ready = Promise.reject(new Error('SignalR client missing'));
            return ready;
        }

        connection = new signalR.HubConnectionBuilder()
            .withUrl(HUB_URL)
            .withAutomaticReconnect()
            .configureLogging(signalR.LogLevel.Warning)
            .build();

        connection.on('ReceiveMessage', dispatch);

        ready = connection.start()
            .then(() => { console.log('[SignalR] verbonden met', HUB_URL, 'als tenant', TENANT); })
            .catch(err => {
                console.error('[SignalR] start() faalde:', err);
                ready = null; // volgende request mag opnieuw proberen
                throw err;
            });
        return ready;
    }

    function dispatch(taskOrSender, _second, third) {
        if (taskOrSender !== 'NormalRender') return;
        const fileName = third;
        if (typeof fileName !== 'string' || !fileName) return;

        // De hub levert de bestandsnaam zonder .jpg, bv "Render_2_0_383e42_...".
        // Knip de prefix eraf om terug te matchen op onze requestString.
        const requestString = fileName.startsWith('Render_') ? fileName.slice(7) : fileName;
        const sub = subscribers.get(requestString);
        if (!sub) return; // niet van ons (bv. een andere tab op dezelfde tenant)

        subscribers.delete(requestString);
        if (sub.timeoutHandle) clearTimeout(sub.timeoutHandle);
        if (sub.opruimHandle) clearTimeout(sub.opruimHandle);

        try { sub.onReady(`${FILE_BASE}/${fileName}.jpg`, requestString); }
        catch (e) { console.error('[SignalR] onReady error:', e); }
    }

    return {
        name: 'signalr',

        /** URL waarop een al bestaande render te vinden is (cache-check). */
        urlFor(requestString) {
            return `${FILE_BASE}/Render_${requestString}.jpg`;
        },

        /** Verbinding alvast opzetten zodat de handshake parallel aan de prijzen loopt. */
        warmup() {
            return init().catch(() => { /* fout wordt al gelogd in init */ });
        },

        /**
         * @param requestString de kale parameter-tekst (waarmee de aanroeper rekent)
         * @param sendAs        wat er als Raw naar de renderer gaat; met een merk
         *                      staat daar `<merk>|<parameters>` in
         * @param fileKey       onder welke naam het resultaat terugkomt; met een
         *                      merk is dat `<merk>_<parameters>`
         */
        async request(requestString, { onReady, onTimeout, timeoutMs = TIMEOUT_MS,
                                       sendAs, fileKey } = {}) {
            const sleutel = fileKey || requestString;
            const raw = sendAs || requestString;

            // Een herhaalde klik mag geen tweede timeout naast de eerste zetten.
            const existing = subscribers.get(sleutel);
            if (existing && existing.timeoutHandle) clearTimeout(existing.timeoutHandle);

            // De hub meldt terug op de bestandsnaam, dus daar schrijven we ons op
            // in. onReady krijgt wel de kale tekst terug, want daar rekent de
            // configurator mee.
            const sub = {
                onReady: (url) => onReady && onReady(url, requestString),
                onTimeout: () => onTimeout && onTimeout(requestString),
                timeoutHandle: null,
            };
            // Bij een verlopen aanvraag melden we dat wel, maar we blijven
            // luisteren. Een render die er langer over doet dan het budget
            // (koude scene, drukke wachtrij) komt dan alsnog in beeld in plaats
            // van in het niets te verdwijnen. De oude code deed dit met
            // doorpollen; bij de overstap naar push was dat vangnet weggevallen,
            // en dat is precies waarom de eerste render na een herstart bleef hangen.
            sub.timeoutHandle = setTimeout(() => {
                if (subscribers.get(sleutel) !== sub) return;
                sub.verlopen = true;
                try { sub.onTimeout(); }
                catch (e) { console.error('[SignalR] onTimeout error:', e); }
            }, timeoutMs);

            // Wel een harde bovengrens, anders blijven inschrijvingen zich opstapelen
            // bij iemand die lang doorklikt.
            sub.opruimHandle = setTimeout(() => {
                if (subscribers.get(sleutel) === sub) subscribers.delete(sleutel);
            }, timeoutMs * 5);

            subscribers.set(sleutel, sub);

            try {
                await init();
            } catch (_) {
                return; // hub onbereikbaar; de timeout activeert straks de overlay
            }

            const payload = {
                SceneSettings: { Priority: 0, CameraPosition: 0, ForceRefresh: false },
                SceneStructure: { Raw: raw },
            };

            try {
                const err = await connection.invoke(
                    'MakeRenderForTenant', 'FrontClient', TENANT, JSON.stringify(payload)
                );
                if (err) console.error('[SignalR] hub error:', err);
            } catch (e) {
                console.error('[SignalR] invoke faalde:', e);
            }
        },

        /**
         * De hub kent nog geen server-side annuleren. We schrijven ons lokaal uit
         * zodat een laat antwoord de UI niet meer aanraakt; de render zelf loopt
         * op de worker gewoon af.
         */
        cancel(keepStrings = []) {
            for (const [rs, sub] of subscribers) {
                if (keepStrings.includes(rs)) continue;
                if (sub.timeoutHandle) clearTimeout(sub.timeoutHandle);
                if (sub.opruimHandle) clearTimeout(sub.opruimHandle);
                subscribers.delete(rs);
            }
        },
    };
}
